'use client';

import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { Skeleton } from 'components/ui/skeleton';
import { StyleGuide } from 'styles/StyleGuide';
import { DateRange } from 'react-day-picker';
import { DateRangePicker } from 'components/ui/date-range-picker';
import { subDays } from 'date-fns';
import { useLevelSensorData } from './hooks/useLevelSensorData';
import { useTranslation } from 'react-i18next';
import { getIsoWeekSummary, usageByWeek } from 'general/usage';
import type { TContainerType } from './containerTypes';
import WaterUsageGraph from './WaterUsageGraph';
import TempGraph from './TempGraph';
import { formatRoundedLiterValue, formatRoundedYAxisTick } from './chartFormatters';

const getColorForValue = (
  value: number,
  min: number,
  max: number,
  fromHue: number = 240, // start color (blue)
  toHue: number = 0 // end color (red)
) => {
  const ratio = (value - min) / (max - min);
  const hue = fromHue + (toHue - fromHue) * ratio;
  return `hsl(${hue}, 100%, 50%)`;
};

export default function LevelChart({
  id,
  containerType,
  thresholds,
}: {
  id: string;
  containerType?: TContainerType;
  thresholds?: { [key: string]: number };
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });

  // Use React Query hook for data fetching
  const { data, isLoading, error } = useLevelSensorData(id, date);

  // Extract data from React Query result
  const dustbinderLevelData = data?.levelData || [];
  const weekSummaryLevelData = data?.weekSummaryLevelData || dustbinderLevelData;
  const waterLiterData = data?.literData || [];
  const tempData = data?.tempData || [];
  const maxLevel = data?.maxLevel || 100;
  const maxLiter = data?.maxLiter || 100;
  const latestLog = data?.latestLog;

  const usage = usageByWeek(weekSummaryLevelData, {
    start: date?.from,
    end: date?.to,
    toleranceCm: 0.2,
    containerType,
  });
  // Format date for X-axis
  const formatXAxis = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[300px] w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 p-6 text-center">
        <p className="text-destructive">Error loading chart data: {error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap gap-2">
        <DateRangePicker date={date} onSelect={setDate} />
        <div className="flex items-center gap-2 ml-auto text-sm text-muted-foreground">
          <div className="flex flex-row items-center">
            <div
              className="w-3 h-3 rounded-full mr-1 inline-block"
              style={{
                backgroundColor: StyleGuide.palette.infoDark,
              }}
            />
            <span className="text-xs">Støvbinder niveau (cm)</span>
          </div>
          <div className="flex flex-row items-center">
            <div
              className="w-3 h-3 rounded-full mr-1 inline-block"
              style={{
                backgroundColor: StyleGuide.palette.processing,
              }}
            />
            <span className="text-xs">Vandforbrug (l)</span>
          </div>
          -<span>{dustbinderLevelData.length} logs vist</span>
        </div>
      </div>

      {/* <div className="px-2">
        <Slider
          defaultValue={[0]}
          max={100}
          step={1}
          onValueChange={handleRangeChange}
          className="my-4"
        />
      </div> */}

      <div className="flex flex-wrap rounded-lg border bg-card p-6">
        <div className="flex w-full flex-row gap-2 justify-end">
          {usage.map((week) => {
            const summary = getIsoWeekSummary(week.week);

            return (
              <div className="text-xs flex flex-col" key={week.weekStart.toISOString()}>
                <div className="text-right text-muted-foreground">{summary.year}</div>
                <div className="text-right">
                  {t('general.week')} {summary.week}
                </div>
                <div className="text-right text-sm font-semibold">
                  {formatRoundedLiterValue(week.usageLiters)}
                </div>
              </div>
            );
          })}
        </div>
        <ChartContainer
          config={{
            waterLevel: {
              label: '(cm)',
              color: StyleGuide.palette.infoDark, // Blue color for water
            },
          }}
          className="h-[300px] w-full"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dustbinderLevelData}
              margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
              accessibilityLayer
              barSize={
                dustbinderLevelData.length > 100 ? 2 : dustbinderLevelData.length > 50 ? 5 : 10
              }
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="timestamp"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                tickFormatter={formatXAxis}
                interval={Math.floor(dustbinderLevelData.length / 10)} // Show fewer ticks for readability
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tickLine={false}
                tickCount={9}
                axisLine={false}
                tickMargin={10}
                allowDecimals={false}
                tickFormatter={formatRoundedYAxisTick}
                domain={[0, maxLevel]}
                label={{ value: 'Level (cm)', angle: -90, position: 'insideLeft' }}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, data) => data[0]?.payload.formattedTime || ''}
                    formatter={(value, name) => {
                      if (name === 'level') {
                        return [`${value} cm`];
                      }
                      return [value, name];
                    }}
                  />
                }
              />
              <Bar
                dataKey="level"
                fill="var(--color-waterLevel)"
                radius={[4, 4, 0, 0]}
                name="level"
              />
              {/* <Bar dataKey="level">
                {visibleData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColorForValue(entry.level, 0, maxLevel, 240, 0)}
                  />
                ))}
              </Bar> */}
              {thresholds?.binderMin && (
                <ReferenceLine
                  y={thresholds.binderMin}
                  stroke={StyleGuide.palette.danger}
                  strokeDasharray="2 2"
                  label=""
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
      <WaterUsageGraph waterLiterData={waterLiterData} maxLiter={maxLiter} />
      <TempGraph tempData={tempData} />

      {latestLog && (
        <div className="text-sm text-muted-foreground">
          <p>Seneste log: {latestLog.formattedTime}</p>
          <p>Device ID: {id}</p>
          <p>
            {t('sensor.details.level.title')}: {latestLog.level} cm
          </p>
        </div>
      )}
    </div>
  );
}
